import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './Settings.css';

/**
 * AdminTokenManagement - Super admin UI for managing admin license tokens
 * Allows creating, viewing, and revoking ADM- tokens
 */
const AdminTokenManagement = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { isSuperAdmin, isLoading: isUserLoading } = useUser();
  const navigate = useNavigate();
  const notification = useNotification();

  const [tokens, setTokens] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [revokingToken, setRevokingToken] = useState(null);
  const [copiedTokenId, setCopiedTokenId] = useState(null);

  // New token form data
  const [newTokenForm, setNewTokenForm] = useState({
    email: '',
    max_locations: 5,
    notes: '',
    expires_in_days: '',
  });

  // Redirect non-super-admins
  useEffect(() => {
    if (!isUserLoading && !isSuperAdmin()) {
      notification.error('Only Super Admins can access this page');
      navigate('/dashboard');
    }
  }, [isSuperAdmin, isUserLoading, navigate, notification]);

  // Fetch tokens
  const fetchTokens = async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/admin-tokens`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTokens(response.data.tokens || response.data);
    } catch (error) {
      console.error('Error fetching tokens:', error);
      notification.error('Failed to load admin tokens');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users awaiting admin access (onboarding_status = 'token_pending')
  const fetchPendingUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/pending-admin-access`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingUsers(response.data.users || response.data);
    } catch (error) {
      console.error('Error fetching pending users:', error);
      // Don't show error notification - endpoint might not exist yet
      setPendingUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchTokens();
      fetchPendingUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewTokenForm({ ...newTokenForm, [name]: value });
  };

  // Open create modal with pre-filled email
  const handleCreateTokenForUser = (user) => {
    setNewTokenForm({
      email: user.email,
      max_locations: 5,
      notes: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
      expires_in_days: '',
    });
    setShowCreateModal(true);
  };

  // Create new token
  const handleCreateToken = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!newTokenForm.email || !newTokenForm.email.trim()) {
      notification.error('User email is required');
      return;
    }

    if (!newTokenForm.max_locations || newTokenForm.max_locations < 1) {
      notification.error('Number of locations must be at least 1');
      return;
    }

    setIsCreating(true);

    try {
      const authToken = await getAccessTokenSilently();
      const payload = {
        email: newTokenForm.email.trim(),
        max_locations: parseInt(newTokenForm.max_locations, 10),
      };

      if (newTokenForm.notes) payload.notes = newTokenForm.notes;
      if (newTokenForm.expires_in_days) {
        payload.expires_in_days = parseInt(newTokenForm.expires_in_days, 10);
      }

      await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/admin-tokens`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      notification.success('Admin token created successfully');
      setShowCreateModal(false);
      setNewTokenForm({ email: '', max_locations: 5, notes: '', expires_in_days: '' });
      fetchTokens();
      fetchPendingUsers(); // Refresh to update "Token Created" status
    } catch (error) {
      console.error('Error creating token:', error);
      notification.error(error.response?.data?.error || 'Failed to create token');
    } finally {
      setIsCreating(false);
    }
  };

  // Revoke token
  const handleRevokeClick = (token) => {
    setRevokingToken(token);
    setIsRevokeModalOpen(true);
  };

  const handleRevoke = async () => {
    if (!revokingToken) return;

    try {
      const authToken = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/admin-tokens/${revokingToken._id || revokingToken.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      notification.success('Token revoked successfully');
      setIsRevokeModalOpen(false);
      setRevokingToken(null);
      fetchTokens();
    } catch (error) {
      console.error('Error revoking token:', error);
      notification.error(error.response?.data?.error || 'Failed to revoke token');
    }
  };

  // Copy token to clipboard
  const handleCopyToken = async (tokenValue) => {
    try {
      await navigator.clipboard.writeText(tokenValue);
      setCopiedTokenId(tokenValue);
      notification.success('Token copied to clipboard');
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (error) {
      notification.error('Failed to copy token');
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'unused': return 'pending';
      case 'used': return 'active';
      case 'revoked': return 'inactive';
      default: return '';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isUserLoading || isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <h1 className="settings-page-title">Admin License Tokens</h1>
            <p className="settings-page-subtitle">Loading...</p>
          </div>
        </div>
        <div className="settings-page-content">
          <div className="settings-card">
            <div className="settings-loading">Loading tokens...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        open={isRevokeModalOpen}
        title="Revoke Token"
        description={`Are you sure you want to revoke this token? ${
          revokingToken?.email ? `It was assigned to ${revokingToken.email}.` : ''
        } This action cannot be undone.`}
        onConfirm={handleRevoke}
        onCancel={() => { setIsRevokeModalOpen(false); setRevokingToken(null); }}
        confirmText="Revoke"
        cancelText="Cancel"
      />

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Admin License Token</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateToken}>
              <div className="modal-body">
                <div className="settings-form-group">
                  <label className="settings-form-label">
                    User Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    className="settings-form-input"
                    name="email"
                    value={newTokenForm.email}
                    onChange={handleFormChange}
                    placeholder="customer@example.com"
                    required
                  />
                  <p className="settings-form-hint">
                    The user must have already signed up with this email. Only they can redeem this token.
                  </p>
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">
                    Number of Locations <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="settings-form-input"
                    name="max_locations"
                    value={newTokenForm.max_locations}
                    onChange={handleFormChange}
                    min="1"
                    max="1000"
                    placeholder="Enter number of locations"
                  />
                  <p className="settings-form-hint">
                    How many store locations this license allows
                  </p>
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">
                    Expiration <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(optional)</span>
                  </label>
                  <select
                    className="settings-form-select"
                    name="expires_in_days"
                    value={newTokenForm.expires_in_days}
                    onChange={handleFormChange}
                  >
                    <option value="">Never expires</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="365">1 year</option>
                  </select>
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">
                    Notes <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(internal)</span>
                  </label>
                  <input
                    type="text"
                    className="settings-form-input"
                    name="notes"
                    value={newTokenForm.notes}
                    onChange={handleFormChange}
                    placeholder="e.g., Fresh Mart - January promotion"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="settings-btn secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settings-btn primary"
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create Token'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="settings-page-header">
        <div className="settings-page-header-left">
          <button className="settings-back-btn" onClick={() => navigate('/settings/organizations')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Organizations
          </button>
          <h1 className="settings-page-title">Admin License Tokens</h1>
          <p className="settings-page-subtitle">
            Generate and manage license tokens for new administrators
          </p>
        </div>
        <div className="settings-page-header-right">
          <button className="settings-btn primary" onClick={() => setShowCreateModal(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Generate New Token
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="settings-page-content">
        {/* Pending Users Section */}
        {pendingUsers.length > 0 && (
          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">
                Users Awaiting Admin Access
                <span className="settings-tab-count" style={{ marginLeft: '8px' }}>
                  {pendingUsers.length}
                </span>
              </h3>
              <p className="settings-card-subtitle">
                These users have signed up and completed their profile. Create a token to grant them admin access.
              </p>
            </div>
            <table className="settings-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Country</th>
                  <th>Signed Up</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((user) => {
                  const userId = user._id || user.id || user.user_id;
                  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'N/A';

                  // Check if a token already exists for this user
                  const existingToken = tokens.find(
                    (t) => t.email === user.email && t.status === 'unused'
                  );

                  return (
                    <tr key={userId}>
                      <td>
                        <div className="settings-user-info">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="settings-user-avatar" />
                          ) : (
                            <div className="settings-user-avatar-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                              </svg>
                            </div>
                          )}
                          <span className="settings-user-name">{fullName}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.phone || '-'}</td>
                      <td>{user.country || '-'}</td>
                      <td>
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : '-'
                        }
                      </td>
                      <td>
                        {existingToken ? (
                          <span className="settings-status-badge pending">
                            Token Created
                          </span>
                        ) : (
                          <button
                            className="settings-btn primary"
                            onClick={() => handleCreateTokenForUser(user)}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                            Create Token
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Tokens Table */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">Admin License Tokens</h3>
            <p className="settings-card-subtitle">
              Tokens that have been created for users
            </p>
          </div>
          <table className="settings-table">
            <thead>
              <tr>
                <th>Token</th>
                <th>Status</th>
                <th>User Email</th>
                <th>Locations</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => {
                const tokenId = token._id || token.id;
                const isUsed = token.status === 'used';
                const isRevoked = token.status === 'revoked';

                return (
                  <tr key={tokenId} className={isRevoked ? 'inactive-user' : ''}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <code style={{
                          background: '#f3f4f6',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                        }}>
                          {token.token.substring(0, 20)}...
                        </code>
                        <button
                          className="settings-action-btn primary"
                          onClick={() => handleCopyToken(token.token)}
                          title="Copy full token"
                          disabled={isRevoked}
                        >
                          {copiedTokenId === token.token ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className={`settings-status-badge ${getStatusBadgeClass(token.status)}`}>
                        {token.status.charAt(0).toUpperCase() + token.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <span className="settings-table-primary">{token.email}</span>
                      {isUsed && (
                        <div style={{ fontSize: '12px', color: '#059669' }}>
                          Redeemed
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="settings-count-badge">{token.max_locations}</span>
                    </td>
                    <td>{formatDate(token.created_at)}</td>
                    <td>
                      {token.expires_at ? (
                        <span style={{
                          color: new Date(token.expires_at) < new Date() ? '#dc2626' : '#374151'
                        }}>
                          {formatDate(token.expires_at)}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>Never</span>
                      )}
                    </td>
                    <td>
                      <div className="settings-table-actions">
                        {!isUsed && !isRevoked && (
                          <button
                            className="settings-action-btn danger"
                            onClick={() => handleRevokeClick(token)}
                            title="Revoke token"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="15" y1="9" x2="9" y2="15" />
                              <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                          </button>
                        )}
                        {isUsed && token.organization_id && (
                          <button
                            className="settings-action-btn primary"
                            onClick={() => navigate(`/settings/organizations/${token.organization_id}`)}
                            title="View organization"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {tokens.length === 0 && (
                <tr>
                  <td colSpan="7" className="settings-table-empty">
                    No admin tokens created yet.
                    {pendingUsers.length > 0
                      ? ' Select a user above to create their token.'
                      : ' Click "Generate New Token" to create one.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      {/* Modal Styles */}
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .modal-close {
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #6b7280;
          border-radius: 4px;
        }

        .modal-close:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .modal-body {
          padding: 24px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .settings-table-empty {
          text-align: center;
          padding: 40px 20px;
          color: #6b7280;
        }
      `}</style>
    </div>
  );
};

export default AdminTokenManagement;
